import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import FileUploadService from '../../../shared/services/file-upload/file-upload.service';
import FileUpload from '../../presentation-components/miscellaneous-components/file-upload';

export const ConversionFactorsUploadDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (args: { file: File; year: string; gwpVersion: string; conversionSourceId: string, conversionType: 'conversion' | 'squareFootConversion' | 'spendBasedConversion', sheetNumber: number; headerRow: number, }) => void;
  availableYears: string[];
  conversionSourceId: string;
  conversionType: 'conversion' | 'squareFootConversion' | 'spendBasedConversion';
}> = ({
  open,
  onClose,
  onSubmit,
  availableYears,
  conversionSourceId,
  conversionType,
}) => {
    //Service
    const fileUploadService = new FileUploadService();
    //State
    const [fileData, setFileData] = useState<File | null>(null);
    const [year, setYear] = useState<string>('');
    // Which IPCC Assessment Report the factors express non-CO2 gases against. Required,
    // and deliberately not defaulted: it is a disclosure, and the 2026 import shipped with
    // it blank because nothing here asked for it. A DEFRA year is usually a mixture, which
    // is why 'AR4/AR5' is offered rather than only the single versions.
    const [gwpVersion, setGwpVersion] = useState<string>('');
    const [sheetNumber, setSheetNumber] = useState<number | ''>(1);
    const [headerRow, setHeaderRow] = useState<number | ''>(1);
    const [numberOfSheetsInFile, setNumberOfSheetsInFile] = useState<number | undefined>(undefined);

    useEffect(() => {
      setYear('');
      setGwpVersion('');
      setFileData(null);
      setSheetNumber(1);
      setHeaderRow(1);
      setNumberOfSheetsInFile(undefined);
    }, [open]);


    const handleFileUpload = (file: File) => {
      setFileData(file);
      if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel')) {
        const form = new FormData();
        form.append('files', file);
        fileUploadService.getNumberOfSheetsInExcelFile(form)
          .then((res: any) => {
            setNumberOfSheetsInFile(res.numberOfSheets);
            setSheetNumber(prev => {
              if (prev === '') return prev;
              const current = typeof prev === 'number' ? prev : Number(prev);
              const clampedMin = Math.max(1, current);
              return res.numberOfSheets ? Math.min(clampedMin, res.numberOfSheets) : clampedMin;
            });
          })
          .catch(() => {
            setNumberOfSheetsInFile(undefined);
          });
      } else {
        setNumberOfSheetsInFile(undefined);
      }
    };

    const handleSubmit = () => {
      if (fileData && year && gwpVersion) {
        const results = {
          file: fileData,
          year,
          gwpVersion,
          conversionSourceId,
          conversionType,
          sheetNumber: typeof sheetNumber === 'number' ? sheetNumber : 1,
          headerRow: typeof headerRow === 'number' ? headerRow : 1,
        }
        onSubmit(results);
      }
    };

    return (
      <Dialog open={open} maxWidth='sm' fullWidth>
        <Box sx={{ p: 4 }}>
          <DialogTitle sx={{ p: 0 }}>Import Conversion Factors</DialogTitle>
          <DialogContent sx={{ pt: 4, pl: 0, pr: 0 }}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <FileUpload csv excel allowedFileTypes={['CSV', 'XLSX']} handleFileUpload={handleFileUpload} />
            </Box>
            <Typography variant='body1' sx={{ mb: 2, fontWeight: 'bold' }}>* Please ensure there is a heading for each column, otherwise the import will not work</Typography>
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel htmlFor='conversionYear'>Conversion Year</InputLabel>
              <Select
                id='conversionYear'
                name='conversionYear'
                label='Conversion Year'
                value={year}
                onChange={(e: SelectChangeEvent<string>) => setYear(e.target.value)}
              >
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id='gwp-version-label'>GWP basis</InputLabel>
              <Select
                labelId='gwp-version-label'
                label='GWP basis'
                value={gwpVersion}
                onChange={(e: SelectChangeEvent<string>) => setGwpVersion(e.target.value)}
              >
                <MenuItem value='AR4'>AR4</MenuItem>
                <MenuItem value='AR4/AR5'>AR4/AR5 (mixed, typical for DEFRA 2021 onward)</MenuItem>
                <MenuItem value='AR5'>AR5</MenuItem>
                <MenuItem value='AR6'>AR6</MenuItem>
              </Select>
              <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5 }}>
                Check the source's methodology paper. DEFRA years are usually a mixture: for 2026,
                well-to-tank bioenergy is AR4 while most other categories are AR5. For a derived
                source, use the basis its derivation records (the EXIOBASE script prints the
                indicator label: IPCC 2007 is AR4, IPCC 2013 is AR5, IPCC 2021 is AR6).
              </Typography>
            </FormControl>

            <TextField
              sx={{ mb: 2 }}
              fullWidth
              variant='outlined'
              id='sheetNumber'
              name='sheetNumber'
              label='Sheet Number'
              type='number'
              inputProps={{ min: 1, ...(numberOfSheetsInFile ? { max: numberOfSheetsInFile } : {}) }}
              value={sheetNumber}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                if (val === '') { setSheetNumber(''); return; }
                const clampedMin = Math.max(1, val);
                const clamped = numberOfSheetsInFile ? Math.min(clampedMin, numberOfSheetsInFile) : clampedMin;
                setSheetNumber(clamped);
              }}
            />
            <TextField
              sx={{ mb: 2 }}
              fullWidth
              variant='outlined'
              id='headerRow'
              name='headerRow'
              label='Header Row'
              type='number'
              inputProps={{ min: 1 }}
              value={headerRow}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                if (val === '') { setHeaderRow(''); return; }
                setHeaderRow(Math.max(1, val));
              }}
            />
          </DialogContent>
          <DialogActions>
            <Stack direction='row' spacing={1}>
              <Button variant='text' onClick={onClose}><Typography sx={{ p: 0.5 }}>Cancel</Typography></Button>
              <Button onClick={handleSubmit} disabled={!fileData || !year || !gwpVersion || sheetNumber === '' || headerRow === ''} sx={{ minWidth: '100px' }}>
                <Typography>Upload</Typography>
              </Button>
            </Stack>
          </DialogActions>
        </Box>
      </Dialog>
    );
  };

